import type { FieldPath, FieldValues } from "react-hook-form";

import { FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface FormInputFieldProps<TFieldValues extends FieldValues> {
  name: FieldPath<TFieldValues>;
  placeholder?: string;
  type?: "text" | "email" | "tel";
  disabled?: boolean;
  className?: string;
}

/**
 * Lightweight RHF-bound input for the occupant drawer. Renders just the control
 * and its error — the surrounding block owns the label — so it drops cleanly
 * into the destination rows, where the labels are column headings.
 */
const FormInputField = <TFieldValues extends FieldValues>({
  name,
  placeholder,
  type = "text",
  disabled,
  className,
}: FormInputFieldProps<TFieldValues>) => (
  <FormField<TFieldValues>
    name={name}
    render={({ field }) => {
      const value = typeof field.value === "string" ? field.value : "";

      return (
        <FormItem className="gap-1">
          <FormControl>
            <Input
              {...field}
              className={cn(className)}
              disabled={disabled}
              placeholder={placeholder}
              type={type}
              value={value}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      );
    }}
  />
);

export default FormInputField;
